import { ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Observable, Subject, Subscription, takeUntil } from 'rxjs';
import { CustomerData } from 'src/app/models/customer.model';
import { KnBrCommonService } from 'src/app/services/kn-br-common.service';
import { KnBrCustomerContextService } from 'src/app/store/kn-br-customer-context/kn-br-customer-context.service';
import { ARRIVING_TODAY, ARRIVING_TOMORROW, NEXT_7_DAYS } from 'src/constants/commonConstants';
import { Statuses } from '../models/kn-br-quote.model';
import { KN_BR_CUSTOMER_CURRENT } from 'src/constants/api.endpoints.constant';
import { GlobalMessageService, GlobalMessageType } from '@spartacus/core';

@Component({
  selector: 'knbr-kn-br-shipment-arrivals',
  templateUrl: './kn-br-shipment-arrivals.component.html',
  styleUrl: './kn-br-shipment-arrivals.component.scss',
  standalone: false,
})
export class KnBrShipmentArrivalsComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();

  brandList: string[] = [];
  pageLoadshippingListCount: any = 0;
  shippingList: any[] = [];

  shippingList$: Observable<any> = this.knBrCommonService.loadShippingData$();
  customerContextData$: Observable<CustomerData> = this.knBrCustomerContextService.get$;
  shipmentDataLoading$ = this.knBrCommonService.shipmentPageLoading$;
  brandSalesShipToList$: Observable<Statuses[]> = this.knBrCommonService.loadBrandSalesShipToList$();
  customerNumber = '';
  customerName = '';
  selectedCustomer: CustomerData;
  subscription = new Subscription();
  searchResult = false;

  // counts for page load global result
  pageLoadtodayCount = 0;
  pageLoadtomorrowCount = 0;
  pageLoaddayAfterTomorrowCount = 0;
  shipmentArrivalForm!: FormGroup;

  constructor(
    protected knBrCustomerContextService: KnBrCustomerContextService,
    protected knBrCommonService: KnBrCommonService,
    protected fb: FormBuilder,
    protected cdr: ChangeDetectorRef,
    private globalMessageService: GlobalMessageService,
  ) { }

  ngOnInit(): void {
    this.initForm();
    this.handleCustomerContext();
    //this.loadInitialData();
    this.listenToShippingList();
  }

  initForm() {
    this.shipmentArrivalForm = this.fb.group({
      orderNumber: [null, Validators.required],
    });
  }

  get shipmentCounts() {
    return [
      {
        label: ARRIVING_TODAY,
        value: this.pageLoadtodayCount,
      },
      {
        label: ARRIVING_TOMORROW,
        value: this.pageLoadtomorrowCount,
      },
      {
        label: NEXT_7_DAYS,
        value: this.pageLoaddayAfterTomorrowCount,
      },
    ];
  }
  get truckStats() {
    const list = this.shippingList || [];

    const stats = [];
    stats.push({
      label: 'Total Trucks',
      value: list.length,
    });
    const today = new Date();

    today.setHours(0, 0, 0, 0);

    let scheduledCount = 0;

    for (let i = 0; i < 7; i++) {
      const target = new Date(today);
      target.setDate(today.getDate() + i);

      target.setHours(0, 0, 0, 0);

      const count = list.filter((item) => {
        const shipmentDate = new Date(item.deliveryDate);

        shipmentDate.setHours(0, 0, 0, 0);

        return shipmentDate.getTime() === target.getTime();
      }).length;

      scheduledCount += count;

      let label = '';

      if (i === 0) {
        label = 'Today';
      } else if (i === 1) {
        label = 'Tomorrow';
      } else {
        label = target.toLocaleDateString('en-US', {
          weekday: 'long',
        });
      }

      stats.push({
        label,
        value: count,
      });
    }

    // Anything after next 7 days
    stats.push({
      label: 'Outstanding',
      value: list.length - scheduledCount,
    });

    return stats;
  }

  private handleCustomerContext() {
    this.customerContextData$.pipe(takeUntil(this.destroy$)).subscribe((value) => {
      if (!value) return;

      this.selectedCustomer = value;
      this.customerNumber = value.customerNumber || '';
      this.customerName = value.customerName;
      this.knBrCommonService.getBrandSalesShipToList(this.customerNumber || KN_BR_CUSTOMER_CURRENT);
    });
    this.subscription.add(this.brandSalesShipToList$.subscribe((res: any) => {
      this.brandList = [];
      if (res && res.brand) {
        res.brand.forEach(item => this.brandList.push(item.value));
      }
      this.loadInitialData();
    }));
  }

  private listenToShippingList() {
    this.shippingList$.pipe(takeUntil(this.destroy$)).subscribe((res) => {
      const { orderNumber } = this.shipmentArrivalForm.getRawValue();
      if (res?.length) {
        if (orderNumber) {
          this.shippingList = this.processOrderSearchShippingList(res);
        } else {
          this.shippingList = [];
          const pageLoadResult = this.processPageLoadShippingList(res);
          this.pageLoadshippingListCount = pageLoadResult?.length;
          this.shippingList = pageLoadResult;
          this.cdr.detectChanges();
        }
      } else if (orderNumber) {
        this.shippingList = [];
      } else {
        this.shippingList = [];
        this.pageLoadtodayCount = 0;
        this.pageLoadtomorrowCount = 0;
        this.pageLoaddayAfterTomorrowCount = 0;
        this.pageLoadshippingListCount = 0;
      }
    });
  }

  private loadInitialData() {
    const payload = this.buildPayload();
    this.knBrCommonService.getShippingHistory(payload);
  }

  private buildPayload(includeOrder = false, isDownload = false) {
    const { fromDate, toDate } = this.getDateRange();
    const { orderNumber } = this.shipmentArrivalForm.getRawValue();
    if (orderNumber && !isDownload) {
      return {
        accountNumber: this.customerNumber,
        brand: this.brandList,
        sourcePageName: 'order',
        ...(includeOrder && orderNumber && { salesOrderNumber: orderNumber }),
      };
    } else {
      return {
        accountNumber: this.customerNumber,
        fromDate,
        toDate,
        brand: this.brandList,
	sourcePageName: 'delivery'
      };
    }
  }

  private getDateRange() {
    const today = new Date();
    const futureDate = new Date();
    futureDate.setDate(today.getDate() + 6);

    return {
      fromDate: this.formatDate(today),
      toDate: this.formatDate(futureDate),
    };
  }

  private formatDate(d: Date): string {
    return (
      `${String(d.getMonth() + 1).padStart(2, '0')}/` +
      `${String(d.getDate()).padStart(2, '0')}/` +
      `${d.getFullYear()}`
    );
  }

  private processPageLoadShippingList(list: any[]) {
    const today = new Date();
    const tomorrow = new Date();
    tomorrow.setDate(today.getDate() + 1);

    const todayStr = this.formatDate(today);
    const tomorrowStr = this.formatDate(tomorrow);

    // reset counts
    this.pageLoadtodayCount = 0;
    this.pageLoadtomorrowCount = 0;
    this.pageLoaddayAfterTomorrowCount = 0;

    const processed = list.map((item) => {
      const itemDate = this.formatDate(new Date(item.deliveryDate));

      let label = 'Scheduled';
      let order = 3;

      if (itemDate === todayStr) {
        this.pageLoadtodayCount++;
        label = ARRIVING_TODAY;
        order = 1;
      } else if (itemDate === tomorrowStr) {
        this.pageLoadtomorrowCount++;
        label = ARRIVING_TOMORROW;
        order = 2;
      } else {
        this.pageLoaddayAfterTomorrowCount++;
      }

      return { ...item, label, order };
    });

    return processed.sort((a, b) =>
      a.order !== b.order ? a.order - b.order : new Date(a.deliveryDate).getTime() - new Date(b.deliveryDate).getTime()
    );
  }

  private processOrderSearchShippingList(list: any[]) {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    return list
      .map((item) => {
        const shipmentDate = new Date(item.deliveryDate);
        shipmentDate.setHours(0, 0, 0, 0);

        const diffDays = Math.floor((shipmentDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));

        let label = 'Scheduled';
        let order = 4;
        if (diffDays === 0) {
          label = ARRIVING_TODAY;
          order = 1;
        } else if (diffDays === 1) {
          label = ARRIVING_TOMORROW;
          order = 2;
        } else if (diffDays >= 2 && diffDays <= 6) {
          label = 'Scheduled'
          order = 3;
        }

        return {
          ...item,
          diffDays,
          label,
          order
        };
      })
      .sort((a, b) => a.order !== b.order ? a.order - b.order : new Date(a.deliveryDate).getTime() - new Date(b.deliveryDate).getTime());
  }

  submitForm() {
    this.searchResult = true;
    if (this.shipmentArrivalForm.valid) {
      const payload = this.buildPayload(true);
      this.knBrCommonService.getShippingHistory(payload);
    }
  }

  resetForm() {
    this.searchResult = false;
    this.shipmentArrivalForm.reset();
    const payload = this.buildPayload();
    this.knBrCommonService.getShippingHistory(payload);
  }

  downloadFile(shippingItem: any) {
    const shipmentDetails = {
      documentName: 'Shipment',
      documentNumber: shippingItem?.deliveryId,
      documentReference: 'V2',
    };

    this.knBrCommonService.downloadSinglePDF(shipmentDetails);
  }

  openTrackingLink(linkUrl) {
    window.open(linkUrl, '_blank');
  }

  downloadShipment() {
    const payload = this.buildPayload(false, true);
    this.knBrCommonService.arrivingSoonMultiDownload(true, 'ArrivingSoon_Export', payload);
  }

  ngOnDestroy() {
    this.knBrCommonService.shippingData$.next([]);
    this.destroy$.next();
    this.destroy$.complete();
    this.subscription.unsubscribe();
  }
}
